import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { HeardIndex, MemberList, PathList, MemberFooter, IfShow, SearchPanel } from '../../components';
import { SearchPageContext } from '../../context';
import { SEARCH_PAGE_ACT_TYPE, SEARCH_TYPE, IMemberInfo, SEARCH_PAGE_TYPE, ISearchPageState } from '../../interface';
import { getKnowledgeDepartmentSearchList, onIsRoot, defaultPage, getWorkspaceDepartmentSearchList } from './dataFlow';
import { components } from 'common';
import { SWITCH_STATUS } from '../../interface';
import { useSize } from 'ahooks';
import './index.scss';
import { defaultPageInfo } from '../../service/defaultData/knowledgePoint';
import { IPageInfo } from '@umu/umu-types';
import { lang } from '@umu/ukit-lang';

const { Loading, If } = components;
interface IIndexPageProps {
    onSelect?: (item: IMemberInfo[]) => void;
    onBack?: () => void;
    onSubmit: (info: ISearchPageState) => void;
}
const getElementChildren = (pageInfo: IPageInfo) => {
    const isMore = pageInfo.total_page_num > pageInfo.current_page;
    if (pageInfo.total_page_num <= 1) {
        return <></>;
    }
    return (
        <div className="element-load-tips-info">
            <If condition={isMore}>
                <div className="load-more">{lang('下滑加载更多')}</div>
            </If>
            <If condition={!isMore}>
                <div className="load-end-info">
                    <div className="split-line" />
                    <div className="split-line-text">{lang('已加载全部')}</div>
                    <div className="split-line" />
                </div>
            </If>
        </div>
    );
};
function IndexPage(props: IIndexPageProps) {
    const { onBack, onSubmit, onSelect } = props;
    const { state, dispatch } = useContext(SearchPageContext);
    const [isLoadingMember, setIsLoadingMember] = useState(false);
    const refDiv = useRef(null);
    const onPageTypeChange = (data: SEARCH_PAGE_TYPE) => {
        dispatch({
            type: SEARCH_PAGE_ACT_TYPE.INIT_DATA,
            payload: {
                pageType: data,
            },
        });
    };
    const onInit = async (searchId: string, isRoot: SWITCH_STATUS, page: number) => {
        const { searchType } = state.paramsInfo;
        switch (searchType) {
            case SEARCH_TYPE.WORKSPACE_DEPARTMENT: {
                const isLoadMore = page > defaultPage;
                const data = await getWorkspaceDepartmentSearchList(searchId, isRoot, page);
                const newData = {
                    ...data,
                    list: isLoadMore ? [...(state.workspaceDepartmentInfo?.list || []), ...data.list] : data.list,
                };

                dispatch({
                    type: SEARCH_PAGE_ACT_TYPE.INIT_DATA,
                    payload: {
                        isLoading: false,
                        workspaceDepartmentInfo: newData,
                        workspaceDepartmentParams: {
                            searchId,
                            isRoot,
                            page,
                        },
                    },
                });
                break;
            }
            case SEARCH_TYPE.KNOWLEDGE_POINT_DEPARTMENT: {
                const isLoadMore = page > defaultPage;
                const data = await getKnowledgeDepartmentSearchList(searchId, isRoot, page);
                const newData = {
                    ...data,
                    list: isLoadMore
                        ? [...(state.dataKnowledgePointDepartmentInfo?.list || []), ...data.list]
                        : data.list,
                };

                dispatch({
                    type: SEARCH_PAGE_ACT_TYPE.INIT_DATA,
                    payload: {
                        isLoading: false,
                        dataKnowledgePointDepartmentInfo: newData,
                        dataKnowledgePointDepartmentParams: {
                            searchId,
                            isRoot,
                            page,
                        },
                    },
                });
                break;
            }
            default:
                break;
        }
    };
    useEffect(() => {
        onInit(state.paramsInfo.searchId, onIsRoot(state.paramsInfo.searchId), defaultPage);
    }, []);

    const [isShowIndex, isShowSearch] = useMemo(() => {
        const isLoaded = !state.isLoading;
        return [
            isLoaded && state.pageType === SEARCH_PAGE_TYPE.INDEX,
            isLoaded && state.pageType === SEARCH_PAGE_TYPE.SEARCH,
        ];
    }, [state.pageType, state.isLoading]);

    /**
     * @desc 选择部门树成员
     */
    const onSelectItem = (checked: boolean, item: IMemberInfo) => {
        const { selectMemberList = [], paramsInfo } = state;
        const isMultiple = paramsInfo.isMultiple;
        let newSelectMemberList: IMemberInfo[] = [];
        if (checked) {
            const isHas = selectMemberList.some((selectItem) => selectItem.id === item.id);
            const newList = isMultiple ? (isHas ? [...selectMemberList] : [...selectMemberList, item]) : [item];
            newSelectMemberList = newList;
        } else {
            newSelectMemberList = isMultiple ? selectMemberList.filter((selectItem) => selectItem.id !== item.id) : [];
        }
        dispatch({
            type: SEARCH_PAGE_ACT_TYPE.UPDATE_LIST,
            payload: {
                selectMemberList: newSelectMemberList,
            },
        });
        onSelect && onSelect(newSelectMemberList);
    };
    /**
     * @desc 选择搜索成员
     */
    const onSearchChange = (val: IMemberInfo[]) => {
        dispatch({
            type: SEARCH_PAGE_ACT_TYPE.UPDATE_SELECT_LIST,
            payload: {
                selectMemberList: val,
                pageType: SEARCH_PAGE_TYPE.INDEX,
            },
        });
    };
    const size = useSize(refDiv);
    const footWidth = useMemo(() => {
        return (size?.width || 700) - 34;
    }, [size?.width]);
    const getListByPage = async (page: number, callBack: VoidFunction) => {
        if (state.dataKnowledgePointDepartmentParams) {
            const { searchId = '', isRoot = false } = state.dataKnowledgePointDepartmentParams;
            await onInit(searchId, isRoot ? SWITCH_STATUS.YES : SWITCH_STATUS.NO, page);
        }
        if (state.workspaceDepartmentParams) {
            const { searchId = '', isRoot = false } = state.workspaceDepartmentParams;
            await onInit(searchId, isRoot ? SWITCH_STATUS.YES : SWITCH_STATUS.NO, page);
        }
        callBack && callBack();
    };
    const onSearchDepartmentMember = async (searchId: string, isRoot: SWITCH_STATUS, page: number) => {
        setIsLoadingMember(true);
        await onInit(searchId, isRoot, page);
        setIsLoadingMember(false);
    };

    const elementProps = {
        [SEARCH_TYPE.KNOWLEDGE_POINT_DEPARTMENT]: {
            path: state.dataKnowledgePointDepartmentInfo?.path || [],
            pageInfo: state.dataKnowledgePointDepartmentInfo?.page_info || defaultPageInfo,
            list: state.dataKnowledgePointDepartmentInfo?.list || []
        },
        [SEARCH_TYPE.WORKSPACE_DEPARTMENT]: {
            path: state.workspaceDepartmentInfo?.path || [],
            pageInfo: state.workspaceDepartmentInfo?.page_info || defaultPageInfo,
            list: state.workspaceDepartmentInfo?.list || []
        },
    }[state.paramsInfo.searchType];

    return (
        <div ref={refDiv}>
            <IfShow condition={isShowIndex} className="comment-search-index-page">
                <HeardIndex onBack={onBack} onSearch={() => onPageTypeChange(SEARCH_PAGE_TYPE.SEARCH)} />
                <IfShow condition={!isLoadingMember}>
                    <PathList
                        list={elementProps.path}
                        onSelectItem={(id, isRoot) =>
                            onSearchDepartmentMember(id, isRoot ? SWITCH_STATUS.YES : SWITCH_STATUS.NO, defaultPage)
                        }
                    />
                    <MemberList
                        list={elementProps.list}
                        selectList={state.selectMemberList || []}
                        pageInfo={elementProps.pageInfo}
                        path={elementProps.path}
                        onSelectNext={(id) => onSearchDepartmentMember(id, SWITCH_STATUS.NO, defaultPage)}
                        onSelectItem={onSelectItem}
                        getListByPage={getListByPage}
                        isMultiple={state.paramsInfo.isMultiple}
                    >
                        {getElementChildren(elementProps.pageInfo)}
                    </MemberList>
                </IfShow>
                <IfShow condition={isLoadingMember}>
                    <Loading />
                </IfShow>
                <MemberFooter
                    style={{ width: footWidth }}
                    maxWidth={footWidth}
                    selectList={state.selectMemberList || []}
                    onSubmit={() => onSubmit(state)}
                    onItemDelete={(item) => onSelectItem(false, item)}
                />
            </IfShow>
            <IfShow condition={isShowSearch} className="member-comment-search-search-page">
                <SearchPanel
                    defaultSelectList={state.selectMemberList || []}
                    onBack={() => onPageTypeChange(SEARCH_PAGE_TYPE.INDEX)}
                    onChange={onSearchChange}
                />
            </IfShow>
            <If condition={state.isLoading}>
                <Loading />
            </If>
        </div>
    );
}

export { IndexPage, IIndexPageProps };
